import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from '../../public Func/axiosAuth';

import { SearchBar, LoadingSkeleton, Button, Modal } from '../../components/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faBan,
  faCircleCheck,
  faUsers,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import './Communities.css';

function CommunitiesPage() {
  const [communityList, setCommunityList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Add modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '', iconLink: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [iconUploading, setIconUploading] = useState(false);
  const iconInputRef = useRef(null);

  // Delete confirm modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Action error
  const [actionError, setActionError] = useState('');

  const fetchCommunities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(process.env.REACT_APP_BACKEND_URL + '/admin/communities');
      setCommunityList(res.data);
    } catch (err) {
      console.error('Error fetching communities:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommunities();
  }, []);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return communityList;
    const q = searchQuery.toLowerCase();
    return communityList.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q)
    );
  }, [communityList, searchQuery]);

  // ----- Add Community -----
  const handleAddOpen = () => {
    setAddForm({ name: '', description: '', iconLink: '' });
    setAddError('');
    setIconUploading(false);
    setAddModalOpen(true);
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAddError('Please select an image file.');
      return;
    }
    setIconUploading(true);
    setAddError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + '/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const url = typeof res.data === 'string' ? res.data : (res.data?.link || res.data?.file || res.data?.filename || res.data);
      setAddForm((prev) => ({ ...prev, iconLink: url }));
    } catch (err) {
      setAddError(err.response?.data || 'Failed to upload icon. Please try again.');
    } finally {
      setIconUploading(false);
      // reset so the same file can be re-selected
      e.target.value = '';
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.description.trim()) {
      setAddError('Name and description are required.');
      return;
    }
    if (!addForm.iconLink) {
      setAddError('Please upload a community icon.');
      return;
    }
    setAddLoading(true);
    setAddError('');
    try {
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + '/admin/community',
        addForm
      );
      setCommunityList((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setAddModalOpen(false);
    } catch (err) {
      setAddError(err.response?.data?.message || 'Failed to create community.');
    } finally {
      setAddLoading(false);
    }
  };

  // ----- Toggle Disable/Enable -----
  const handleToggleDisabled = async (community) => {
    setActionError('');
    const newState = !community.isDisabled;
    try {
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + '/admin/community/set-disabled',
        { communityID: community.id, isDisabled: newState }
      );
      setCommunityList((prev) =>
        prev.map((c) => (c.id === community.id ? res.data : c))
      );
    } catch (err) {
      setActionError(err.response?.data?.message || 'Action failed.');
    }
  };

  // ----- Delete Community -----
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(
        process.env.REACT_APP_BACKEND_URL + '/admin/community?communityID=' + deleteTarget.id
      );
      setCommunityList((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to delete community.');
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const enabledCount  = communityList.filter((c) => !c.isDisabled).length;
  const disabledCount = communityList.filter((c) => c.isDisabled).length;

  return (
    <div className="communities-page">
      {/* Header */}
      <div className="communities-header">
        <div>
          <h1 className="page-title">Communities</h1>
          <p className="page-subtitle">
            {loading
              ? 'Loading...'
              : `${communityList.length} total · ${enabledCount} active · ${disabledCount} disabled`}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          leftIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={handleAddOpen}
        >
          Add Community
        </Button>
      </div>

      {/* Global action error */}
      {actionError && (
        <div className="communities-error-banner">
          {actionError}
          <button className="error-banner-close" onClick={() => setActionError('')}>✕</button>
        </div>
      )}

      {/* Search */}
      <div className="communities-search">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search communities..."
          size="lg"
          fullWidth
          disabled={loading}
        />
      </div>

      {/* Grid */}
      <div className="communities-grid">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="community-card-skeleton">
              <LoadingSkeleton width="56px" height="56px" />
              <LoadingSkeleton width="70%" height="22px" />
              <LoadingSkeleton width="100%" height="40px" />
              <LoadingSkeleton width="100%" height="36px" />
            </div>
          ))
        ) : filtered.length > 0 ? (
          filtered.map((community) => (
            <CommunityCard
              key={community.id}
              community={community}
              onToggleDisabled={handleToggleDisabled}
              onDelete={(c) => { setActionError(''); setDeleteTarget(c); }}
            />
          ))
        ) : (
          <div className="communities-empty">
            <FontAwesomeIcon icon={faUsers} className="empty-icon" />
            <h3>No communities found</h3>
            <p>{searchQuery ? 'Try adjusting your search.' : 'No communities yet. Add one above.'}</p>
          </div>
        )}
      </div>

      {/* Add Community Modal */}
      <Modal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add Community"
        size="sm"
        footer={
          <div className="modal-footer-actions">
            <Button variant="ghost" onClick={() => setAddModalOpen(false)} disabled={addLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddSubmit}
              loading={addLoading}
            >
              Create
            </Button>
          </div>
        }
      >
        <form onSubmit={handleAddSubmit} className="add-community-form">
          {addError && <div className="form-error">{addError}</div>}
          <div className="form-field">
            <label>Name *</label>
            <input
              type="text"
              value={addForm.name}
              onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
              placeholder="e.g. Mental Health"
              maxLength={80}
              required
            />
          </div>
          <div className="form-field">
            <label>Description *</label>
            <textarea
              value={addForm.description}
              onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
              placeholder="Brief description of this community..."
              rows={3}
              maxLength={300}
              required
            />
          </div>
          <div className="form-field">
            <label>Community Icon *</label>
            <input
              ref={iconInputRef}
              type="file"
              accept="image/*"
              onChange={handleIconUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className={`icon-upload-btn ${iconUploading ? 'icon-upload-btn--loading' : ''}`}
              onClick={() => iconInputRef.current.click()}
              disabled={iconUploading}
            >
              <FontAwesomeIcon icon={faImage} />
              {iconUploading ? 'Uploading...' : addForm.iconLink ? 'Change Icon' : 'Upload Icon'}
            </button>
            {addForm.iconLink && (
              <div className="icon-preview-wrap">
                <img src={addForm.iconLink} alt="Icon preview" className="icon-preview" />
                <button
                  type="button"
                  className="icon-preview-remove"
                  onClick={() => setAddForm((prev) => ({ ...prev, iconLink: '' }))}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </form>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Community"
        size="sm"
        footer={
          <div className="modal-footer-actions">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              loading={deleteLoading}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className="delete-confirm-text">
          Are you sure you want to permanently delete{' '}
          <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
}

function CommunityCard({ community, onToggleDisabled, onDelete }) {
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    await onToggleDisabled(community);
    setToggling(false);
  };

  return (
    <div className={`community-card ${community.isDisabled ? 'community-card--disabled' : ''}`}>
      {/* Icon + Status badge */}
      <div className="community-card-top">
        <div className="community-icon-wrap">
          <img
            src={community.iconLink}
            alt={community.name}
            className="community-icon"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
        <span className={`community-status-badge ${community.isDisabled ? 'badge--disabled' : 'badge--active'}`}>
          <FontAwesomeIcon icon={community.isDisabled ? faBan : faCircleCheck} />
          {community.isDisabled ? 'Disabled' : 'Active'}
        </span>
      </div>

      {/* Info */}
      <div className="community-info">
        <h3 className="community-name">{community.name}</h3>
        <p className="community-description">{community.description}</p>
      </div>

      {/* Actions */}
      <div className="community-actions">
        <Button
          variant={community.isDisabled ? 'success' : 'warning'}
          size="sm"
          fullWidth
          leftIcon={<FontAwesomeIcon icon={community.isDisabled ? faCircleCheck : faBan} />}
          loading={toggling}
          onClick={handleToggle}
        >
          {community.isDisabled ? 'Enable' : 'Disable'}
        </Button>
        <Button
          variant="danger"
          size="sm"
          leftIcon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => onDelete(community)}
        >
          Remove
        </Button>
      </div>
    </div>
  );
}

export default CommunitiesPage;
